import React, { useState, useEffect } from 'react';
import { 
  Settings as SettingsIcon, 
  Clock, 
  Save, 
  Filter,
  Plus,
  X,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Info,
  Zap,
  Target,
  Building2,
  Cpu,
  Languages,
  Globe,
  ExternalLink,
  Tag
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../components/ui/alert';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function SettingsPage() {
  const navigate = useNavigate();
  const [schedule, setSchedule] = useState({
    hour: 8,
    minute: 0,
    enabled: true
  });
  const [crawlerSettings, setCrawlerSettings] = useState({
    custom_exclusions: [],
    require_core_match: false,
    min_support_matches: 1,
    boost_title_matches: true,
    exclusion_patterns_count: 0
  });
  // Keywords summary from Keywords page (unified source)
  const [keywordsSummary, setKeywordsSummary] = useState({
    alephtav: { total: 0, english: 0, amharic: 0, keywords: [] },
    mautech: { total: 0, english: 0, amharic: 0, keywords: [] }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingCrawler, setSavingCrawler] = useState(false);
  const [newExclusion, setNewExclusion] = useState('');
  const [testTenderTitle, setTestTenderTitle] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  // Helper to detect Amharic characters
  const isAmharic = (word) => /[\u1200-\u137F]/.test(word);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [scheduleRes, crawlerRes, alephtavKeywordsRes, mautechKeywordsRes] = await Promise.all([
        fetch(`${API_URL}/api/config/schedule`),
        fetch(`${API_URL}/api/crawler-settings`),
        fetch(`${API_URL}/api/keywords/ALEPHTAV`),
        fetch(`${API_URL}/api/keywords/MAU_TECH`)
      ]);
      
      const scheduleData = await scheduleRes.json();
      const crawlerData = await crawlerRes.json();
      
      // Get keywords summary from Keywords page (unified source)
      if (alephtavKeywordsRes.ok && mautechKeywordsRes.ok) {
        const alephtavData = await alephtavKeywordsRes.json();
        const mautechData = await mautechKeywordsRes.json();
        
        const alephtavKeywords = alephtavData.keywords || [];
        const mautechKeywords = mautechData.keywords || [];
        
        setKeywordsSummary({
          alephtav: {
            total: alephtavKeywords.length,
            english: alephtavKeywords.filter(k => !isAmharic(k)).length,
            amharic: alephtavKeywords.filter(k => isAmharic(k)).length,
            keywords: alephtavKeywords.slice(0, 10) // Preview first 10
          },
          mautech: {
            total: mautechKeywords.length,
            english: mautechKeywords.filter(k => !isAmharic(k)).length,
            amharic: mautechKeywords.filter(k => isAmharic(k)).length,
            keywords: mautechKeywords.slice(0, 10)
          }
        });
      }
      
      setSchedule({
        hour: scheduleData.hour ?? 8,
        minute: scheduleData.minute ?? 0,
        enabled: scheduleData.enabled ?? true
      });
      
      setCrawlerSettings(crawlerData);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSchedule = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/config/schedule`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(schedule)
      });

      if (response.ok) {
        toast.success('Schedule updated', {
          description: schedule.enabled
            ? `Daily run at ${schedule.hour.toString().padStart(2, '0')}:${schedule.minute.toString().padStart(2, '0')} EAT`
            : 'Automated runs disabled'
        });
      } else {
        toast.error('Failed to update schedule');
      }
    } catch (error) {
      toast.error('Error saving schedule');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCrawlerSettings = async () => {
    setSavingCrawler(true);
    try {
      const response = await fetch(`${API_URL}/api/crawler-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          custom_exclusions: crawlerSettings.custom_exclusions,
          require_core_match: crawlerSettings.require_core_match,
          min_support_matches: crawlerSettings.min_support_matches,
          boost_title_matches: crawlerSettings.boost_title_matches
        })
      });

      if (response.ok) {
        toast.success('Crawler settings saved', {
          description: 'Classification policies updated successfully'
        });
      } else {
        toast.error('Failed to save crawler settings');
      }
    } catch (error) {
      toast.error('Error saving crawler settings');
    } finally {
      setSavingCrawler(false);
    }
  };

  const addExclusion = () => {
    const term = newExclusion.trim();
    if (!term) return;

    if (crawlerSettings.custom_exclusions.includes(term.toLowerCase())) {
      toast.error('Exclusion already exists');
      return;
    }

    setCrawlerSettings(prev => ({
      ...prev,
      custom_exclusions: [...prev.custom_exclusions, term.toLowerCase()]
    }));
    setNewExclusion('');
  };

  const removeExclusion = (term) => {
    setCrawlerSettings(prev => ({
      ...prev,
      custom_exclusions: prev.custom_exclusions.filter(t => t !== term)
    }));
  };

  const testClassification = async () => {
    if (!testTenderTitle.trim()) {
      toast.error('Please enter a tender title to test');
      return;
    }

    setTesting(true);
    try {
      const response = await fetch(`${API_URL}/api/crawler-settings/test-classification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: testTenderTitle,
          category: '',
          description: '',
          buyer: '',
          deadline: ''
        })
      });

      const result = await response.json();
      setTestResult(result);
    } catch (error) {
      toast.error('Error testing classification');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-spinner" data-testid="settings-loading">
        <RefreshCw className="animate-spin" size={32} />
        <p>Loading settings...</p>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div data-testid="settings-page" className="settings-page">
        <div className="section-header">
          <div>
            <h2 className="section-title">Settings</h2>
            <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: 14, marginTop: 4 }}>
              Configure crawler behavior and scheduled runs
            </p>
          </div>
        </div>

        <Tabs defaultValue="crawler" className="settings-tabs">
          <TabsList className="settings-tabs-list" data-testid="settings-tabs">
            <TabsTrigger value="crawler" data-testid="tab-crawler">
              <Filter size={16} />
              <span>Crawler Settings</span>
            </TabsTrigger>
            <TabsTrigger value="schedule" data-testid="tab-schedule">
              <Clock size={16} />
              <span>Schedule</span>
            </TabsTrigger>
            <TabsTrigger value="system" data-testid="tab-system">
              <SettingsIcon size={16} />
              <span>System Info</span>
            </TabsTrigger>
          </TabsList>

          {/* Crawler Settings Tab */}
          <TabsContent value="crawler" className="settings-tab-content">
            
            {/* Unified Keywords Info */}
            <Alert className="info-alert" data-testid="unified-keywords-alert" style={{ marginBottom: 24, borderColor: '#3b82f6', backgroundColor: '#eff6ff' }}>
              <Info size={18} style={{ color: '#3b82f6' }} />
              <AlertTitle style={{ color: '#1e40af' }}>Unified Keyword System</AlertTitle>
              <AlertDescription style={{ color: '#1e40af' }}>
                Keywords are now managed in <strong>one place</strong> — the Keywords page. 
                These keywords are used for <strong>both</strong> searching tender sites AND classifying results.
              </AlertDescription>
            </Alert>

            {/* Keywords Summary Card */}
            <Card className="settings-card" data-testid="keywords-summary-card" style={{ marginBottom: 24 }}>
              <CardHeader className="settings-card-header">
                <div className="card-header-content">
                  <div className="card-icon" style={{ background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', color: 'white' }}>
                    <Tag size={20} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <CardTitle>Search & Classification Keywords</CardTitle>
                    <CardDescription>
                      Keywords used by scrapers to search tender sites and classify results
                    </CardDescription>
                  </div>
                  <Button 
                    onClick={() => navigate('/keywords')}
                    data-testid="manage-keywords-btn"
                  >
                    <ExternalLink size={14} />
                    Manage Keywords
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                  {/* Alephtav Summary */}
                  <div className="keyword-summary-box" style={{ padding: 16, borderRadius: 8, background: '#e3f2fd', border: '1px solid #90caf9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                      <Building2 size={18} style={{ color: '#1565c0' }} />
                      <h4 style={{ fontWeight: 600, color: '#1565c0', margin: 0 }}>Alephtav</h4>
                      <Badge variant="secondary">{keywordsSummary.alephtav.total} keywords</Badge>
                    </div>
                    <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                        <Globe size={14} />
                        <span>{keywordsSummary.alephtav.english} English</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                        <Languages size={14} style={{ color: '#f59e0b' }} />
                        <span>{keywordsSummary.alephtav.amharic} Amharic</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {keywordsSummary.alephtav.keywords.slice(0, 6).map((kw, i) => (
                        <Badge key={i} variant="outline" style={{ fontSize: 11 }}>
                          {isAmharic(kw) && <Languages size={10} style={{ marginRight: 2 }} />}
                          {kw}
                        </Badge>
                      ))}
                      {keywordsSummary.alephtav.total > 6 && (
                        <Badge variant="secondary" style={{ fontSize: 11 }}>+{keywordsSummary.alephtav.total - 6} more</Badge>
                      )}
                    </div>
                  </div>

                  {/* Mau Tech Summary */}
                  <div className="keyword-summary-box" style={{ padding: 16, borderRadius: 8, background: '#e8f5e9', border: '1px solid #a5d6a7' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                      <Cpu size={18} style={{ color: '#2e7d32' }} />
                      <h4 style={{ fontWeight: 600, color: '#2e7d32', margin: 0 }}>Mau Tech</h4>
                      <Badge variant="secondary">{keywordsSummary.mautech.total} keywords</Badge>
                    </div>
                    <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                        <Globe size={14} />
                        <span>{keywordsSummary.mautech.english} English</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                        <Languages size={14} style={{ color: '#f59e0b' }} />
                        <span>{keywordsSummary.mautech.amharic} Amharic</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {keywordsSummary.mautech.keywords.slice(0, 6).map((kw, i) => (
                        <Badge key={i} variant="outline" style={{ fontSize: 11 }}>
                          {isAmharic(kw) && <Languages size={10} style={{ marginRight: 2 }} />}
                          {kw}
                        </Badge>
                      ))}
                      {keywordsSummary.mautech.total > 6 && (
                        <Badge variant="secondary" style={{ fontSize: 11 }}>+{keywordsSummary.mautech.total - 6} more</Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Custom Exclusions */}
            <Card className="settings-card" data-testid="exclusions-card">
              <CardHeader className="settings-card-header">
                <div className="card-header-content">
                  <div className="card-icon exclusion-icon">
                    <Filter size={20} />
                  </div>
                  <div>
                    <CardTitle className="card-title-with-badge">
                      Custom Exclusions
                      <Badge variant="secondary" className="term-count-badge">
                        {crawlerSettings.custom_exclusions?.length || 0} custom + {crawlerSettings.exclusion_patterns_count || 30} built-in
                      </Badge>
                    </CardTitle>
                    <CardDescription>
                      Add terms to filter out irrelevant tenders (e.g., &lsquo;furniture&rsquo;, &lsquo;cleaning service&rsquo;)
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="search-terms-container">
                  <div className="terms-list" data-testid="exclusions-list">
                    {crawlerSettings.custom_exclusions?.map((term, index) => (
                      <Badge 
                        key={index} 
                        variant="outline" 
                        className="search-term-badge exclusion-term"
                        data-testid={`exclusion-term-${index}`}
                      >
                        {term}
                        <button 
                          onClick={() => removeExclusion(term)}
                          className="remove-term-btn"
                          data-testid={`remove-exclusion-${index}`}
                        >
                          <X size={12} />
                        </button>
                      </Badge>
                    ))}
                    {(!crawlerSettings.custom_exclusions || crawlerSettings.custom_exclusions.length === 0) && (
                      <span className="no-terms-text">No custom exclusions added</span>
                    )}
                  </div>
                  <div className="add-term-row">
                    <Input
                      placeholder="Add exclusion term (e.g., 'medical supply')"
                      value={newExclusion}
                      onChange={(e) => setNewExclusion(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addExclusion()}
                      className="add-term-input"
                      data-testid="new-exclusion-input"
                    />
                    <Button 
                      onClick={addExclusion}
                      size="sm"
                      variant="outline"
                      data-testid="add-exclusion-btn"
                    >
                      <Plus size={14} />
                      Add
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Classification Policies */}
            <Card className="settings-card" data-testid="policies-card">
              <CardHeader className="settings-card-header">
                <div className="card-header-content">
                  <div className="card-icon policy-icon">
                    <Zap size={20} />
                  </div>
                  <div>
                    <CardTitle>Classification Policies</CardTitle>
                    <CardDescription>
                      Fine-tune how tenders are matched and classified
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="policy-options">
                  <div className="policy-item" data-testid="policy-boost-title">
                    <div className="policy-info">
                      <span className="policy-label">Boost Title Matches</span>
                      <span className="policy-description">
                        Give more weight to keywords found in the tender title
                      </span>
                    </div>
                    <Switch
                      checked={crawlerSettings.boost_title_matches}
                      onCheckedChange={(checked) => setCrawlerSettings(prev => ({
                        ...prev,
                        boost_title_matches: checked
                      }))}
                      data-testid="boost-title-switch"
                    />
                  </div>
                  <Separator />
                  <div className="policy-item" data-testid="policy-require-core">
                    <div className="policy-info">
                      <span className="policy-label">Require Core Keyword Match</span>
                      <span className="policy-description">
                        Only classify tenders that match at least one core keyword (stricter filtering)
                      </span>
                    </div>
                    <Switch
                      checked={crawlerSettings.require_core_match}
                      onCheckedChange={(checked) => setCrawlerSettings(prev => ({
                        ...prev,
                        require_core_match: checked
                      }))}
                      data-testid="require-core-switch"
                    />
                  </div>
                  <Separator />
                  <div className="policy-item" data-testid="policy-min-support">
                    <div className="policy-info">
                      <span className="policy-label">Minimum Support Matches</span>
                      <span className="policy-description">
                        Minimum number of supporting keywords required for classification
                      </span>
                    </div>
                    <Input
                      type="number"
                      min={1}
                      max={5}
                      value={crawlerSettings.min_support_matches}
                      onChange={(e) => setCrawlerSettings(prev => ({
                        ...prev,
                        min_support_matches: parseInt(e.target.value) || 1
                      }))}
                      className="policy-number-input"
                      data-testid="min-support-input"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Test Classification */}
            <Card className="settings-card" data-testid="test-classification-card">
              <CardHeader className="settings-card-header">
                <div className="card-header-content">
                  <div className="card-icon test-icon">
                    <Target size={20} />
                  </div>
                  <div>
                    <CardTitle>Test Classification</CardTitle>
                    <CardDescription>
                      Enter a tender title to see how it would be classified
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="test-classification-container">
                  <div className="test-input-row">
                    <Input
                      placeholder="Enter tender title (e.g., 'Supply of Laptop Computers for Ministry of Education')"
                      value={testTenderTitle}
                      onChange={(e) => setTestTenderTitle(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && testClassification()}
                      className="test-input"
                      data-testid="test-classification-input"
                    />
                    <Button 
                      onClick={testClassification}
                      disabled={testing}
                      data-testid="test-classification-btn"
                    >
                      {testing ? (
                        <>
                          <RefreshCw size={14} className="animate-spin" />
                          Testing...
                        </>
                      ) : (
                        <>
                          <Target size={14} />
                          Test
                        </>
                      )}
                    </Button>
                  </div>

                  {testResult && (
                    <div className="test-result" data-testid="test-result">
                      {testResult.is_relevant ? (
                        <div className="test-result-success">
                          <CheckCircle2 size={18} className="result-icon success" />
                          <div className="result-content">
                            <div className="result-header">
                              <Badge className={`company-badge ${testResult.company?.toLowerCase()}`}>
                                {testResult.company === 'ALEPHTAV' ? 'Alephtav' : 'Mau Tech'}
                              </Badge>
                              <Badge variant="outline" className={`priority-badge ${testResult.priority?.toLowerCase()}`}>
                                {testResult.priority} Priority
                              </Badge>
                            </div>
                            <p className="result-rationale">{testResult.rationale}</p>
                            {testResult.matched_terms?.length > 0 && (
                              <div className="matched-terms">
                                <span className="matched-label">Matched:</span>
                                {testResult.matched_terms.map((term, i) => (
                                  <Badge key={i} variant="secondary" className="matched-term">{term}</Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="test-result-fail">
                          <AlertTriangle size={18} className="result-icon fail" />
                          <div className="result-content">
                            <p className="result-not-relevant">Not Relevant</p>
                            <p className="result-rationale">{testResult.rationale}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Save Button */}
            <div className="settings-actions">
              <Button 
                onClick={handleSaveCrawlerSettings} 
                disabled={savingCrawler}
                size="lg"
                data-testid="save-crawler-settings-btn"
              >
                <Save size={16} />
                {savingCrawler ? 'Saving...' : 'Save Crawler Settings'}
              </Button>
            </div>
          </TabsContent>

          {/* Schedule Tab */}
          <TabsContent value="schedule" className="settings-tab-content">
            <Card className="settings-card">
              <CardHeader>
                <CardTitle style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Clock size={20} />
                  Scheduled Runs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ marginBottom: 24 }}>
                  <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: 14, marginBottom: 20 }}>
                    Configure when the tender scout should automatically run. Times are in East Africa Time (EAT / UTC+3).
                  </p>

                  <div className="switch-container" style={{ marginBottom: 20 }}>
                    <Switch
                      checked={schedule.enabled}
                      onCheckedChange={(checked) => setSchedule(prev => ({ ...prev, enabled: checked }))}
                      data-testid="schedule-enabled-switch"
                    />
                    <span style={{ fontWeight: 500 }}>
                      {schedule.enabled ? 'Scheduled runs enabled' : 'Scheduled runs disabled'}
                    </span>
                  </div>

                  {schedule.enabled && (
                    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end' }}>
                      <div className="form-group" style={{ marginBottom: 0, width: 120 }}>
                        <label className="form-label">Hour (0-23)</label>
                        <Input
                          type="number"
                          min={0}
                          max={23}
                          value={schedule.hour}
                          onChange={(e) => setSchedule(prev => ({ ...prev, hour: parseInt(e.target.value) || 0 }))}
                          data-testid="schedule-hour-input"
                        />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0, width: 120 }}>
                        <label className="form-label">Minute (0-59)</label>
                        <Input
                          type="number"
                          min={0}
                          max={59}
                          value={schedule.minute}
                          onChange={(e) => setSchedule(prev => ({ ...prev, minute: parseInt(e.target.value) || 0 }))}
                          data-testid="schedule-minute-input"
                        />
                      </div>
                      <div style={{ paddingBottom: 4, color: 'hsl(var(--muted-foreground))' }}>
                        EAT (UTC+3)
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ borderTop: '1px solid hsl(var(--border))', paddingTop: 20 }}>
                  <Button onClick={handleSaveSchedule} disabled={saving} data-testid="save-schedule-btn">
                    <Save size={14} />
                    {saving ? 'Saving...' : 'Save Schedule'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* System Info Tab */}
          <TabsContent value="system" className="settings-tab-content">
            <Card className="settings-card">
              <CardHeader>
                <CardTitle style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <SettingsIcon size={20} />
                  System Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px 24px', fontSize: 14 }}>
                  <div style={{ color: 'hsl(var(--muted-foreground))' }}>Application</div>
                  <div style={{ fontWeight: 500 }}>TavTenders - Tender Intelligence</div>

                  <div style={{ color: 'hsl(var(--muted-foreground))' }}>Version</div>
                  <div style={{ fontWeight: 500 }}>2.1.0 (Unified Keywords)</div>

                  <div style={{ color: 'hsl(var(--muted-foreground))' }}>Companies</div>
                  <div style={{ fontWeight: 500 }}>Alephtav Consultancy, Mau Tech</div>

                  <div style={{ color: 'hsl(var(--muted-foreground))' }}>Timezone</div>
                  <div style={{ fontWeight: 500 }}>Africa/Addis_Ababa (EAT / UTC+3)</div>

                  <div style={{ color: 'hsl(var(--muted-foreground))' }}>Email Service</div>
                  <div style={{ fontWeight: 500 }}>SendGrid</div>

                  <div style={{ color: 'hsl(var(--muted-foreground))' }}>Active Scrapers</div>
                  <div style={{ fontWeight: 500 }}>2Merkato, Ethiopia eGP, iChereta, EthiopianTender.com, EthioJobs</div>
                  
                  <div style={{ color: 'hsl(var(--muted-foreground))' }}>Keyword System</div>
                  <div style={{ fontWeight: 500 }}>Unified (Keywords Page is single source of truth)</div>
                </div>
              </CardContent>
            </Card>

            <Card style={{ marginTop: 24 }}>
              <CardContent style={{ padding: 20 }}>
                <h3 style={{ fontWeight: 600, marginBottom: 12 }}>About TavTenders</h3>
                <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: 14, lineHeight: 1.8 }}>
                  TavTenders is an intelligent tender scouting and management system designed for
                  Alephtav Consultancy and Mau Tech. It automatically scrapes tender opportunities from
                  multiple Ethiopian tender portals, classifies them based on relevance to each company&apos;s
                  business profile, and sends daily email digests to configured recipients.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </TooltipProvider>
  );
}

export default SettingsPage;
